import { AddScreenDialogProps } from "@components/screens/AddScreenDialog";
import { Button } from "@components/ui/button";
import { ScreenInfo } from "@components/screens/ScreenInfoPage";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useDialog from "@hooks/useDialog";
import { useScreen } from "@hooks/useScreen";
import {
  ScreenWithPlayingInfo,
  ScreenWithPlayingNameQuery,
  useScreens,
} from "@hooks/useScreens";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import { env } from "@src/env.mjs";
import CustomWebSocket from "@utils/customWebSocket";
import { serverEndpoints } from "@utils/serverEndpoints";
import { authClient } from "@utils/authClient";
import { supabase } from "@utils/serverClient";
import Link from "next/link";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import DataFetchStatus from "types/DataFetchStatus";
import { AddScreenReturnProps } from "./types";

const addScreenContext = createContext<AddScreenReturnProps | null>(null);
const { Provider } = addScreenContext;
type Props = {
  children: React.ReactNode;
  props: AddScreenDialogProps;
};

export function AddScreenProvider({ children, props }: Props) {
  const socket = useMemo(
    () =>
      CustomWebSocket.getInstance(
        env.NEXT_PUBLIC_WS_URL +
        "/" +
        env.NEXT_PUBLIC_WS_PAIRING_NAMESPACE +
        "/public/websocket",
      ),
    [],
  );

  const { showErrorSnackbar, showSuccessSnackbar } = useDialog();

  const screen = useScreen()

  const [inputPin, setInputPin] = useState<string | null>(null);
  const [screenInfo, setScreenInfo] = useState<ScreenInfo | undefined>(screen?.screenInfo);
  const [screenCreateStatus, setScreenCreateStatus] = useState<DataFetchStatus>("idle");

  const screenInfoRef = useRef(screenInfo);
  screenInfoRef.current = screenInfo;
  const inputPinRef = useRef(inputPin);
  inputPinRef.current = inputPin;
  const screenCreateStatusRef = useRef(screenCreateStatus);
  screenCreateStatusRef.current = screenCreateStatus;
  const isCreatingScreenRef = useRef(false);

  const onSubmit = async () => {
    if (!inputPin) {
      showErrorSnackbar("Please enter a pin");
      return;
    }

    setScreenCreateStatus("fetching");

    try {
      socket.emit(
        "joinRoomIfPresent",
        {
          roomId: inputPin.toLowerCase(),
        },
        async (val?: boolean) => {
          if (val) {
            // send a message to the room
            socket.emit("requestDeviceId", {
              roomId: inputPin.toLowerCase(),
            });
          } else {
            setScreenCreateStatus("error");

            showErrorSnackbar("Looks like you entered a wrong pin");
            return;
          }
        },
      );
    } catch (e) {
      showErrorSnackbar("Error creating screen");
      setScreenCreateStatus("error");
    }
  };

  const { mutate: createScreen } = useDataMutation({
    mutationFn: async (screen: ScreenInfo & { screenId?: string, width?: string, height?: string, org_id?: string }) => {
      // Check if the user has reached the max screens per location limit
      if (planProperties.max_screens_per_location && screen.locationId) {
        const { data: existingScreens, error: screensError } = await supabase
          .from("screen")
          .select("id")
          .eq("location_id", screen.locationId)
          .eq("org_id", screen.org_id ?? "")
          .eq("deleted", false);

        if (screensError) {
          throw new Error("Error fetching existing screens for location");
        }

        if (existingScreens && existingScreens.length >= planProperties.max_screens_per_location) {
          throw new Error(`You can only have ${planProperties.max_screens_per_location} screen(s) per location in your current plan. Please upgrade your plan to add more.`);
        }
      }

      const accessToken = await authClient.getAccessToken();

      if (!accessToken) return null;

      return fetch(serverEndpoints.createScreen, {
        method: "POST",
        body: JSON.stringify(screen),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then((d) => d.json())
    },
    queryKeyToInvalidate: ["screens", props.org_id],
    options: {
      onError: (error: any) => {
        // If the error is about location limits, show an upgrade dialog
        if (error.message && error.message.includes("You can only have")) {
          openDialog({
            title: "Screen Limit Reached",
            customContent: (
              <div className="flex flex-col gap-2 pt-2">
                <p>
                  {error.message}
                </p>

                <div className="flex w-full items-center justify-end">
                  <Button asChild>
                    <Link href={"/change_plan"}>Upgrade Options</Link>
                  </Button>
                </div>
              </div>
            ),
          });
        }
      },
    },
  })

  useEffect(() => {
    socket.onMessage = async (event) => {
      try {
        const dat = JSON.parse(event.data);
        if (dat.event === "deviceIdRecievedFromScreen") {
          const { deviceId, width, height } = dat.data;

          const currentScreenInfo = screenInfoRef.current;
          if (!currentScreenInfo) return;

          if (isCreatingScreenRef.current) return;
          isCreatingScreenRef.current = true;
          setScreenCreateStatus("fetching");

          createScreen({
            screenId: currentScreenInfo?.id,
            name: currentScreenInfo?.name,
            deviceId: currentScreenInfo?.deviceId ?? deviceId,
            locationId: currentScreenInfo?.locationId,
            screenType: currentScreenInfo?.screenType,
            width: width,
            height: height,
            org_id: props.org_id,
          }, {
            onSuccess(data) {
              socket.emit(
                "sendOrgIdAndDeviceIdToRoomIfPresent",
                {
                  roomId: inputPinRef.current?.toLowerCase() ?? "",
                  orgId: props.org_id,
                  deviceId: data[0].id,
                },
                async (val?: boolean) => {
                  if (val) {
                    setScreenCreateStatus("success");
                    showSuccessSnackbar(
                      `Screen ${props.screen ? "updated" : "added"} successfully`,
                    );
                    socket.close();
                    onSuccess(data[0]);
                  } else {
                    setScreenCreateStatus("error");
                    showErrorSnackbar("Error creating screen");
                    return;
                  }
                },
              );
            },
            onError() {
              showErrorSnackbar(
                `Error ${props.screen ? "updating" : "creating"} screen`,
              );
              isCreatingScreenRef.current = false;
              setScreenCreateStatus("error");
            }
          })
        }
      } catch (e) { }
    };

    return () => {
      socket.onMessage = null;
    };
  }, [socket]);

  const onSuccess: typeof props.onSuccess = async (_screen) => {
    props.onSuccess(_screen)
  }

  const { openDialog } = useDialog();
  const { planProperties } = useUserSubscriptions();
  const { screens } = useScreens()

  const { mutate: onPairLater } = useDataMutation({
    mutationFn: async () => {
      if (!screenInfo?.screenType) return;

      // Check if the user has reached the max screens per location limit
      if (planProperties.max_screens_per_location && screenInfo?.locationId) {
        const existingScreens = screens;

        if (existingScreens && existingScreens.length >= planProperties.max_screens_per_location) {
          throw new Error(`You can only have ${planProperties.max_screens_per_location} screen(s) per location in your current plan. Please upgrade your plan to add more.`);
        }
      }

      const { data, error } = await supabase
        .from("screen")
        .insert({
          name: screenInfo?.name,
          org_id: props.org_id,
          location_id: screenInfo?.locationId,
          screen_type: screenInfo?.screenType,
          deleted: false,
          is_paired: false,
        })
        .select(ScreenWithPlayingNameQuery)
        .single();

      if (error) {
        throw new Error("Error creating screen");
      }

      return data
    },
    errorMessage: "Error creating screen",
    optimisticUpdateFn: (oldData: ScreenWithPlayingInfo[] | undefined, screen: ScreenWithPlayingInfo) => {
      return oldData?.map(s => s?.id === screen?.id ? { ...screen, is_paired: false } : s)
    },
    queryKeyToInvalidate: ["screens", props.org_id],
    options: {
      onSuccess: (screen) => screen && onSuccess(screen),
      onError: (error) => {
        // If the error is about location limits, show an upgrade dialog
        if (error.message.includes("You can only have")) {
          openDialog({
            title: "Screen Limit Reached",
            customContent: (
              <div className="flex flex-col gap-2 pt-2">
                <p>
                  {error.message}
                </p>

                <div className="flex w-full items-center justify-end">
                  <Button asChild>
                    <Link href={"/change_plan"}>Upgrade Options</Link>
                  </Button>
                </div>
              </div>
            ),
          });
        } else {
          console.log(error);
        }
      },
    }
  })

  async function updateScreen(info: ScreenInfo) {
    if (screenCreateStatus === "fetching") return;

    // Check if the user has reached the max screens per location limit when assigning to a new location
    if (planProperties.max_screens_per_location && info.locationId) {
      // If the screen is changing location or being assigned to a location for the first time
      if (info.locationId !== screenInfo?.locationId) {
        // Exclude the current screen being updated
        const existingScreens = screens.filter(s => s.location_id === info.locationId && s.id !== screenInfo?.id);

        if (existingScreens && existingScreens.length >= planProperties.max_screens_per_location) {
          // Show upgrade dialog
          openDialog({
            title: "Screen Limit Reached",
            customContent: (
              <div className="flex flex-col gap-2 pt-2">
                <p>
                  You can only have {planProperties.max_screens_per_location} screen(s) per location in your current plan. Please upgrade your plan to add more.
                </p>

                <div className="flex w-full items-center justify-end">
                  <Button asChild>
                    <Link href={"/change_plan"}>Upgrade Options</Link>
                  </Button>
                </div>
              </div>
            ),
          });
          return;
        }
      }
    }

    setScreenCreateStatus("fetching");

    const { data, error } = await supabase
      .from("screen")
      .update({
        name: info.name,
        location_id: info.locationId,
        screen_type: info.screenType,
        music_playing: info.music_playing,
        last_music_id: info.last_music_id,
        music_infos: info.music_infos,
        zone_id: info.zone_id,
        group_id: info.group_id,
        music_status: info.music_status,
      })
      .match({ id: screenInfo?.id || info.id })
      .select(ScreenWithPlayingNameQuery)
      .returns<ScreenWithPlayingInfo[]>();

    if (!data || error) {
      setScreenCreateStatus("error");
      showErrorSnackbar(
        `Error ${screenInfo ? "updating" : "creating"} screen`,
      );
      return;
    } else {
      setScreenCreateStatus("success");
      socket.close();

      onSuccess(data[0]);
    }
  }

  return (
    <Provider
      value={{
        socket,
        inputPin,
        setInputPin,
        screenInfo,
        setScreenInfo: setScreenInfo!,
        screenCreateStatus,
        setScreenCreateStatus,
        onSubmit,
        updateScreen,
        onPairLater: () => onPairLater(undefined as unknown as ScreenWithPlayingInfo),
      }}
    >
      {children}
    </Provider>
  );
}

export const useAddScreen: () => AddScreenReturnProps = () => {
  const context = useContext(addScreenContext);
  if (!context) throw "useAddScreen must be used within a AddScreenProvider";
  return context;
};
